import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/product.dto';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

export interface ArkifiHubProductWishList {
  id: string;
  productId: string;
  arkifiHubCustomerId: string;
  productName: string;
  productDescription?: string;
  productImage?: string;
  productPrice: number;
  compareAtPrice?: number;
  discountPercentage?: number;
  isInStock: boolean;
  stockQuantity: number;
  productSlug?: string;
  productSku?: string;
  notes?: string;
  priority: number;
  dateAdded: string;
  dateUpdated?: string;
  platformId: string;
  businessId: string;
  environmentType: string;
  categoryName?: string;
  brandName?: string;
}

export interface AddToWishListRequest {
  productId: string;
  notes?: string;
  priority?: number;
}

export interface UpdateWishListItemRequest {
  wishListItemId: string;
  notes?: string;
  priority?: number;
}

export interface WishListSummary {
  totalItems: number;
  totalValue: number;
  averageItemPrice: number;
  inStockItems: number;
  outOfStockItems: number;
  highPriorityItems: number;
  mediumPriorityItems: number;
  lowPriorityItems: number;
  lastUpdated: string;
  dateRangeStart: string;
  dateRangeEnd: string;
}

@Injectable({
  providedIn: 'root'
})
export class WishlistService {
  private apiUrl = `${environment.apiUrl}/api/arkifihubproductwishlist`;
  private wishListCount = signal<number>(0);
  public wishListCount$ = computed(() => this.wishListCount());

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      console.warn('No authentication token found. User may need to log in again.');
    }
    if (token) {
      return new HttpHeaders({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      });
    }
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  // Add product to wishlist
  addToWishList(request: AddToWishListRequest): Observable<ApiResponse<ArkifiHubProductWishList>> {
    return this.http.post<ApiResponse<ArkifiHubProductWishList>>(`${this.apiUrl}/add`, request, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(() => this.updateWishListCount())
    );
  }

  // Remove product from wishlist
  removeFromWishList(wishListItemId: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.apiUrl}/remove/${wishListItemId}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(() => this.updateWishListCount())
    );
  }

  // Update wishlist item
  updateWishListItem(request: UpdateWishListItemRequest): Observable<ApiResponse<ArkifiHubProductWishList>> {
    return this.http.put<ApiResponse<ArkifiHubProductWishList>>(`${this.apiUrl}/update`, request, {
      headers: this.getAuthHeaders()
    });
  }

  // Get customer's wishlist
  getWishList(platformId: string, page: number = 1, pageSize: number = 20): Observable<ApiResponse<ArkifiHubProductWishList[]>> {
    const environment = this.settingsService.getEnvironment();
    let params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ApiResponse<ArkifiHubProductWishList[]>>(this.apiUrl, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  // Get wishlist summary
  getWishListSummary(platformId: string): Observable<ApiResponse<WishListSummary>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<WishListSummary>>(`${this.apiUrl}/summary`, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  // Check if product is in wishlist
  isProductInWishList(productId: string, platformId: string): Observable<ApiResponse<boolean>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<boolean>>(`${this.apiUrl}/check/${productId}`, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  // Get wishlist count
  getWishListCount(platformId: string): Observable<ApiResponse<number>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.get<ApiResponse<number>>(`${this.apiUrl}/count`, {
      headers: this.getAuthHeaders(),
      params: params
    });
  }

  // Clear entire wishlist
  clearWishList(platformId: string): Observable<ApiResponse<boolean>> {
    const environment = this.settingsService.getEnvironment();
    const params = new HttpParams()
      .set('platformId', platformId)
      .set('environment', environment);

    return this.http.delete<ApiResponse<boolean>>(`${this.apiUrl}/clear`, {
      headers: this.getAuthHeaders(),
      params: params
    }).pipe(
      tap(() => this.updateWishListCount())
    );
  }

  // Update wishlist count (private method)
  private updateWishListCount(platformId?: string): void {
    // Note: This requires platformId, so we'll need to call it with platformId when available
    // For now, we'll just set it to 0 if no platformId
    if (!platformId) {
      this.wishListCount.set(0);
      return;
    }

    this.getWishListCount(platformId).subscribe({
      next: (response) => {
        if (response.success) {
          this.wishListCount.set(response.data);
        }
      },
      error: (error) => {
        console.error('Error updating wishlist count:', error);
      }
    });
  }

  // Initialize wishlist count (call this when user logs in)
  initializeWishListCount(platformId: string): void {
    this.updateWishListCount(platformId);
  }

  // Get current wishlist count value
  get currentWishListCount(): number {
    return this.wishListCount();
  }
}
